import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { CommunicationService } from './shared/services/communication.service';
import { Injectable } from '@angular/core';
import { CurrentFolderService } from './shared/services/current-folder.service';

const HOME_PAGE_ROUTE = 'home-page';
const COUNT_OF_CALLS_ANGULAR_MAKES_TO_SHOULDATTACH_WHEN_NAVIGATING_TO_SHP = 2;

// This clsss is modified version of code suggested by Google AI
// (See https://community-dc.max.gov/x/bIWtnw for some backgound on this.)
@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    // The store of routes' components that should be reused when navigating back to them
    private storedRoutes = new Map<string, DetachedRouteHandle>();

    // Added to get shouldAttach() to return false when the Home Page needs to be refreshed
    private doNotAttachHomePage: boolean = false;

    // Added to control the bahavior of shouldAttach() when when the Home Page needs to be refreshed
    // (See comments on its use below)
    private attachHomePagePathCounter = 0;
    constructor(private currentFolderService: CurrentFolderService) { }

    // Added to allow external classes to notify CustomRouteReuseStrategy that when next going to the home-page route it should NOT attach
    // a stored HomePageComponent but instiate a new one from scratch. This is to handle events like creating a new Form which require
    // the HomePageComponent's state to be refreshed. (See https://community-dc.max.gov/x/bIWtnw for some backgound on this.)
    public setDoNotAttachHomePageOnNextNav() {
        this.doNotAttachHomePage = true;
    }

    // Determines if a route should be reused.
    public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        const should = curr.routeConfig?.data?.reuseComponent;
        return should;
    }

    // Called when navigating away from a route to see if its componenent should be detached rather than destroyed
    public shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const should = !!route.routeConfig && route.routeConfig.data && route.routeConfig.data['reuseComponent'];
        return should;
    }

    // Stores a detached route for later reuse
    public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (route.routeConfig) {
            if (handle) {
                // PJH - 3/9/2026 - VNEXT-2290 - the currentFolderServices need to know about
                // routes that have been detached (aka stored and cached) so that it doesn't send
                // messages to componenents belonging to them
                this.currentFolderService.addToDetachedRoutes(route.routeConfig.path);
            }
            this.storedRoutes.set(route.routeConfig.path as string, handle);
        }
    }

    // Called when navigating TO a route to determine if the route's component should be reattached from storage vs. recreated from scratch
    public shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const path = route.routeConfig?.path;
        let should = !!route.routeConfig && this.storedRoutes.has(route.routeConfig.path as string);
        if (path === HOME_PAGE_ROUTE) {
            should = this.checkIfDoNotAttachHomePageIsSet(should);
        }
        return should;
    }

    // PJH - VNEXT-2038
    // This method is not ideal, but after playing around with different approaches that did not pan out, this is what I'm going with
    // (See https://community-dc.max.gov/x/bIWtnw for some backgound on this.)
    // 1) doNotAttachHomePage is set from outside this custom route strategy by operations which require the HomePageComponent to be rebuilt from scratch
    //    (Operations like creating or udpating a Form)
    // 2) attachHomePagePathCounter is necessary because shouldAttach() gets called twice when navigating to the 'home-page' path (this is an Angular thing we're not in control of)
    //    and doNotAttachHomePage should not get set to false until BOTH of these have completed.
    //    This feels brittle, and something that a future version of Angular could break.
    //    (Perhaps it would be better for Form Grids to poll some property in CurrentSiteService or something and display a "This needs to be refreshed" message)
    private checkIfDoNotAttachHomePageIsSet(should: boolean): boolean {
        if (this.doNotAttachHomePage) {
            this.attachHomePagePathCounter++;
            should = false;
            // Only reset doNotAttachHomePage to false after the TWO calls that get made to shouldAttach() when navigating to the home-page route
            if (this.attachHomePagePathCounter == COUNT_OF_CALLS_ANGULAR_MAKES_TO_SHOULDATTACH_WHEN_NAVIGATING_TO_SHP) {
                this.attachHomePagePathCounter = 0;
                this.doNotAttachHomePage = false;
            }
        }
        return should;
    }

    // Retrieves a stored route.
    public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!route.routeConfig || !this.storedRoutes.has(route.routeConfig.path as string)) {
            return null;
        }

        // PJH - 3/9/2026 - VNEXT-2290 
        this.currentFolderService.removeFromDetachedRoutes(route.routeConfig.path);
        return this.storedRoutes.get(route.routeConfig.path as string) as DetachedRouteHandle;
    }
}
